import { RSAA } from 'redux-api-middleware'
import { getApiPath, makeApiTypes } from 'sidecar-utility-library'
import { getGeolocation } from 'sidecar-utilities'

import { types as AppTypes } from 'store/App'
import { normalizeLocation } from './utils'

export const types = {
  SET_MAP_LOCATION: 'location/SET_MAP_LOCATION',
  CLEAR_MAP_LOCATION: 'location/CLEAR_MAP_LOCATION',
  ...makeApiTypes('GET_CURRENT_LOCATION', 'location'),
  ...makeApiTypes('FETCH_ZIP_BY_LAT_LON', 'location'),
  ...makeApiTypes('FETCH_IP_INFO', 'location'),
}

// we actually don't care about the types here because
// we're going to have middlware store the IP info in
// webStorage. This also saves us a rerender.
export const fetchIpInfo = () => ({
  [RSAA]: {
    endpoint: getApiPath(`auth`, `location`),
    method: 'GET',
    headers: {
      'x-api-key': process.env.REACT_APP_X_API_KEY,
    },
    types: [
      types.FETCH_IP_INFO_REQUEST,
      types.FETCH_IP_INFO_SUCCESS,
      types.FETCH_IP_INFO_ERROR,
    ],
  },
})

const tryToGetCurrentLoc = async () => {
  // Race geolocation against a 10s safety net. The actual timeout is
  // handled by the Geolocation API options (5s) passed in getGeolocation,
  // so this outer race only catches truly hung calls.
  return await Promise.race([
    getGeolocation({ component: 'member/store/Location' }),
    new Promise(r =>
      setTimeout(
        () =>
          r({
            error: true,
            response: { message: 'timeout' },
          }),
        10000,
      ),
    ),
  ])
}

export const getCurrentLocation = () => async dispatch => {
  dispatch({
    type: types.GET_CURRENT_LOCATION_REQUEST,
  })

  const geoloc = await tryToGetCurrentLoc()

  if (geoloc.error) {
    if (geoloc.code !== 1) {
      console.debug('Could not get current location:', geoloc.response.message)
    }
    return dispatch({
      type: types.GET_CURRENT_LOCATION_ERROR,
      payload: geoloc,
    })
  }
  return dispatch({
    type: types.GET_CURRENT_LOCATION_SUCCESS,
    payload: { ...geoloc, address: 'Current Location' },
  })
}

export const setMapLocation = payload => ({
  type: types.SET_MAP_LOCATION,
  payload,
})

export const clearMapLocation = () => ({
  type: types.CLEAR_MAP_LOCATION,
})

export const initialState = {
  // this location is the location that the map is currently centered on
  // using a default of 12 (about a 10sq.mi. area) for initial load.
  mapLocation: {
    entity: {
      zoom: 12,
      radius: 8,
    },
  },
  // this location should be the one that we'll get from navigator.getGeolocation.
  // It's our best guess where the user is so it's the preferred default location
  // when user isn't specifically searching. Getting is an async process.
  currentLocation: {
    busy: false,
    error: '',
    entity: {},
  },
  // this location is the one that we get from asking our API about our IP address
  // (/auth/v1/location) It's our second preference for default location. Getting
  // this location is an async process.
  ipLocation: {
    busy: false,
    error: '',
    entity: {},
  },
  // and lastly, if no other address is around, this address is from the coverage
  // mailing address. When all else fails, we assume they want results at least
  // close to their home.
  coverageLocation: {
    entity: {},
  },
}

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    case AppTypes.FETCH_INITIAL_SETUP_REQUEST: {
      return { ...state, ipLocation: { ...state.ipLocation, busy: true } }
    }
    case AppTypes.FETCH_INITIAL_SETUP_SUCCESS: {
      let location = payload.location
      location.address = location.zip || 'Unknown'
      const loc = normalizeLocation(location)
      return {
        ...state,
        ipLocation: {
          ...state.ipLocation,
          busy: false,
          entity: loc,
        },
      }
    }
    case AppTypes.FETCH_INITIAL_SETUP_ERROR: {
      return {
        ...state,
        ipLocation: {
          ...state.ipLocation,
          busy: false,
          error: payload.response.message,
        },
      }
    }

    case types.GET_CURRENT_LOCATION_REQUEST: {
      return {
        ...state,
        currentLocation: { ...state.currentLocation, busy: true },
      }
    }
    case types.GET_CURRENT_LOCATION_SUCCESS: {
      payload.address = 'Current location'
      const loc = normalizeLocation(payload)
      return {
        ...state,
        currentLocation: {
          ...state.currentLocation,
          busy: false,
          entity: loc,
        },
      }
    }
    case types.GET_CURRENT_LOCATION_ERROR: {
      return {
        ...state,
        currentLocation: {
          ...state.currentLocation,
          busy: false,
          error: payload.response.message,
        },
      }
    }

    case types.SET_MAP_LOCATION: {
      // using a type of merge because mergeIn isn't really merging
      // and changing the location without a zoom will force the zoom
      // and radius to become undefined. We want to keep those values.
      const currentLocation = state?.mapLocation?.entity
      const zip = payload.zip || currentLocation.zip

      return {
        ...state,
        mapLocation: {
          ...state.mapLocation,
          entity: { ...currentLocation, ...payload, zip },
        },
      }
    }
    case types.CLEAR_MAP_LOCATION: {
      return {
        ...state,
        mapLocation: {
          ...state.mapLocation,
          entity: {},
        },
      }
    }

    default:
      return state
  }
}
